import {Fragment, useEffect, useRef, useState} from "react";
import { format } from "date-fns";
import classNames from "classnames/bind";
import styles from "./CaseChatCard.module.scss";
import {BreadCrumb, CenteredModal, FormInput, DropdownAction, FilePreview} from "~/components";
import { Send } from "lucide-react";
import CaseChatCard from "./component/CaseChatCard";
import CasePendingCard from "./component/CasePendingCard";
import ChatMessageGroup from "./component/ChatMessageGroup";
import {toast} from "react-toastify";
import * as signalR from "@microsoft/signalr";
import SupportCaseAPI from "~/api/supportCaseApi";
import {useForm} from "react-hook-form";
import { faEye, faFile, faXmark} from "@fortawesome/free-solid-svg-icons";
import { useAssetsImage } from '~/pages/context/AssetsImageContext';

const cx = classNames.bind(styles);

const breadCrumbItem = [
    { text: "AI Chatbot Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Support Case", to: "", isActive: true },
];
const apiUrl = process.env.REACT_APP_API_URL;

function SupportCaseAdmin() {
    const [activeTab, setActiveTab] = useState("PendingTab");
    const [pendingCase, setPendingCase] = useState([]);
    const [chattingCase, setChattingCase] = useState([]);
    const [messageGroup, setMessageGroup] = useState([]);
    const [selectedChat, setSelectedChat] = useState({});
    const [reloadPendingCase, setReloadPendingCase] = useState(false);
    const [messageInput, setMessageInput] = useState("");
    const [allowChat, setAllowChat] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [connection, setConnection] = useState(null);
    const [activeUser, setActiveUser] = useState(null);

    const [isFocused, setIsFocused] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);

    const chatEndRef = useRef(null);
    const [toggleScrollEndChat, setToggleScrollEndChat] = useState(false);

    const [showCaseFileModal, setShowCaseFileModal] = useState(false);
    const [caseFiles, setCaseFiles] = useState([]);

    const selectedChatRef = useRef(selectedChat);

    const {getImageUrlById} = useAssetsImage();
    const noChatImage = getImageUrlById(38);
    const noCaseImage = getImageUrlById(37);
    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm();

    useEffect(() => {
        const connect = new signalR.HubConnectionBuilder()
            .withUrl(`${apiUrl}/chatHub`, {
                withCredentials: true
            })
            .withAutomaticReconnect()
            .build();

        connect.start()
            .then(() => {
                setConnection(connect);
            })
            .catch((err) => {} );

        connect.on("AdminReceiveMessage", async (chatContent, userId) => {
            const chat = selectedChatRef.current;
            if(chat?.UserId === userId) {
                handleMessageChange(false, chatContent, "");
                setToggleScrollEndChat(prev => !prev);
                if (document.hidden) {
                    setHasUnread(true);
                    return;
                }
                if (isFocused) {
                    await connection.invoke(
                        "MarkMessageAsRead",
                        selectedChat.CurrentSessionId,
                        selectedChat.CaseSessionId,
                        true,
                        selectedChat.UserId
                    );
                } else {
                    setHasUnread(true);
                }
            }
            else {
                setChattingCase(prevCases =>
                    prevCases.map(item => {
                        if (item.UserId === userId) {
                            return {
                                ...item,
                                LastChatCustomer: chatContent,
                                CountCustomerChatUnread: (item.CountCustomerChatUnread || 0) + 1
                            };
                        }
                        return item;
                    })
                );
            }
        });

        connect.on("MessageStatusUpdate", (tempId, status) => {
            updateMessageGroupStatus(tempId, status);
        });

        connect.on("MarkAsRead", () => {
            setMessageGroup(prev => {
                const newK = [...prev];
                const lastIndex = newK.length - 1;

                const updatedGroup = newK[lastIndex].map(item =>
                    item.IsRead === false ? { ...item, IsRead: true } : item
                );
                newK[lastIndex] = updatedGroup;
                return newK;
            });
        });

        return () => {
            connect.stop();
        };
    }, [activeUser]);

    const updateMessageGroupStatus = (tempId, status) => {
        setMessageGroup(prev =>
            prev.map(group => {
                let changed = false;
                const updatedGroup = group.map(item => {
                    if (item.TempId === tempId) {
                        changed = true;
                        return { ...item, Status: status };
                    }
                    return item;
                });
                return changed ? updatedGroup : group;
            })
        );
    };


    useEffect(() => {
        const fetchCases = async () => {
            try {
                const res =
                    activeTab === "PendingTab"
                        ? await SupportCaseAPI.getPendingCase()
                        : await SupportCaseAPI.getChattingCase();
                activeTab === "PendingTab"
                    ? setPendingCase(res.data)
                    : setChattingCase(res.data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchCases();
    }, [activeTab, reloadPendingCase]);

    const groupMessagesBySenderSequence = (messages) => {
        const grouped = [];
        let currentGroup = [];

        messages.forEach((msg) => {
            if (
                currentGroup.length === 0 ||
                currentGroup[currentGroup.length - 1].SupporterOrCustomer === msg.SupporterOrCustomer
            ) {
                currentGroup.push(msg);
            } else {
                grouped.push(currentGroup);
                currentGroup = [msg];
            }
        });

        if (currentGroup.length > 0) grouped.push(currentGroup);
        return grouped;
    };

    const handleOnClickChattingCase = async (id) => {
        if (selectedChat.CaseSessionId === id) return;
        setIsFocused(true);
        setSelectedChat(chattingCase.find((x) => x.CaseSessionId === id) || null);
        setAllowChat(true);
        try {
            const res = await SupportCaseAPI.getCaseChatBySession(id);
            setMessageGroup(groupMessagesBySenderSequence(res.data));
            const targetCase = chattingCase.find(
                item => item.CaseSessionId === id && item.CountCustomerChatUnread > 0
            );
            setChattingCase(prevCases =>
                prevCases.map(item =>
                    item.CaseSessionId === id
                        ? { ...item, CountCustomerChatUnread: 0 }
                        : item
                )
            );
            if(targetCase) {
                setHasUnread(true);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if(!allowChat) return;
            handleSendMessage();
        }
    };

    const handleMessageChange = (isSupporter, chatContent, tempId = "") => {
        const currentRole = isSupporter ? "Supporter" : "Customer";
        const newMessage = {
            SupporterOrCustomer: currentRole,
            ChatContent: chatContent,
            CreatedDate: new Date().toISOString(),
            TempId: tempId,
            Status: isSupporter ? "sending" : "",
            IsRead: false
        };

        setMessageGroup(prevMessages => {
            const updatedMessages = [...prevMessages];
            const lastGroup = updatedMessages[updatedMessages.length - 1];
            if (lastGroup && (lastGroup[0].SupporterOrCustomer === currentRole)) {
                updatedMessages[updatedMessages.length - 1] = [...lastGroup, newMessage];
            } else {
                updatedMessages.push([{ ...newMessage }]);
            }
            return updatedMessages;
        });
    }

    const handleSendMessage = async () => {
        if (!messageInput.trim()) return;
        const tempId = "temp_" + Date.now();
        handleMessageChange(true, messageInput, tempId)
        setMessageInput("");
        const newMessage = {
            SupporterOrCustomer: "Supporter",
            ChatContent: messageInput,
            CaseSessionId: selectedChat.CaseSessionId
        };
        await connection.invoke("AdminReply", selectedChat.CurrentSessionId, tempId, selectedChat.UserId, newMessage);
        setToggleScrollEndChat(prev => !prev);
    };

    const handleOnClickAccept = (id, userId, currentSessionId) => {
        SupportCaseAPI.accept(id)
            .then(async (res) =>  {
                if(res.data.Success){
                    setReloadPendingCase(prev => !prev);
                    await connection.invoke("AdminAcceptedChat", userId, true, currentSessionId);
                }
                else{
                    toast.error(<span className="fs-9 fw-bold">An error occurred while accepting support case.</span>)
                }
            })
            .catch(err => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while accepting support case.</span>)
            })
    }

    const handleOnClickReject = (id, userId) => {
        reset({
            SupportCaseId: id,
            UserId: userId,
            RejectReason: ""
        })
        setShowModal(true)
    }

    const handleOnClickDetails = (id) => {
    }

    const onSubmitFrom = (data) => {
        SupportCaseAPI.reject(data)
            .then(async (res) => {
                if(res.data.Success){
                    setReloadPendingCase(prev => !prev);
                    await connection.invoke("AdminAcceptedChat", data.UserId, false, "");
                }
                else{
                    toast.error(<span className="fs-9 fw-bold">An error occurred while rejecting support case.</span>)
                }
            })
            .catch(err => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while rejecting support case.</span>)
            })
            .finally(async () => {
                setShowModal(false)
            })
    }

    const renderSidebarContent = () => {
        const cases = activeTab === "PendingTab" ? pendingCase : chattingCase;

        return (
            <div className={cx("case-tab-content", cases.length <= 0 ? "d-flex flex-center flex-column" : "" , "scrollbar")}>
                {
                    cases.length <= 0 && (
                        <Fragment>
                            {
                                noCaseImage &&
                                <img alt="chat" height="160" width="220" className="mb-3" src={noCaseImage}/>
                            }
                            <span>No support case</span>
                        </Fragment>
                    )
                }

                {cases.map((item, index) =>
                    activeTab === "PendingTab" ? (
                        <CasePendingCard
                            key={index}
                            avatarUrl={item.CustomerAvatarUrl}
                            type={item.CaseContactType}
                            caseCreatedDate={format(new Date(item.CreatedDate), "dd-MM-yyyy hh:mm a")}
                            castType={item.CaseType}
                            senderName={item.CustomerName}
                            onClickAccept={() => handleOnClickAccept(item.SupportCaseId, item.UserId, item.CurrentSessionId)}
                            onClickReject={() => handleOnClickReject(item.SupportCaseId, item.UserId)}
                            onClickDetails={() => handleOnClickDetails(item.SupportCaseId)}
                        />
                    ) : (
                        <CaseChatCard
                            key={index}
                            avatarUrl={item.CustomerAvatarUrl}
                            status={item.CountCustomerChatUnread > 0 ? "awaiting" : "active"}
                            active={item.CaseSessionId == selectedChat.CaseSessionId}
                            supportCaseId={`#${item.CaseId}`}
                            caseTitle={item.Subject}
                            lastMessage={item.LastChatCustomer}
                            senderName={item.CustomerName}
                            onClick={() => handleOnClickChattingCase(item.CaseSessionId)}
                            unreadCount = {item.CountCustomerChatUnread}
                        />
                    )
                )}
            </div>
        );
    };

    useEffect(() => {
        if (isFocused && hasUnread && Object.keys(selectedChat).length > 0) {
            connection.invoke(
                "MarkMessageAsRead",
                selectedChat.CurrentSessionId,
                selectedChat.CaseSessionId,
                true,
                selectedChat.UserId
            );
            setHasUnread(false);
        }
    }, [isFocused, hasUnread]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [toggleScrollEndChat]);

    const handleCloseChat = async () => {
        SupportCaseAPI.resolved(selectedChat.SupportCaseId)
            .then(async (res) => {
                if(res.data?.Success){
                    toast.success(<span className="fs-9 fw-bold">Resolved support case successfully.</span>)
                    setChattingCase(prev => {
                        const updated = prev.filter(x => x.SupportCaseId !== selectedChat.SupportCaseId);
                        if (updated.length > 0) {
                            handleOnClickChattingCase(updated[0].CaseSessionId);
                        } else {
                            setSelectedChat({});
                            setMessageGroup([]);
                        }
                        return updated;
                    });
                    await connection.invoke("AdminEndChat", selectedChat.UserId);
                }
                else{
                    toast.error(<span className="fs-9 fw-bold">An error occurred while resolving support case.</span>)
                }
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while resolving support case.</span>)
            })
    }

    const handleOpenCaseFile = () => {
        const params = new URLSearchParams({
            supportCaseId: selectedChat.SupportCaseId,
            caseSessionId: selectedChat.CaseSessionId
        });
        SupportCaseAPI.getCaseFileByCaseSession(params)
            .then((res) => {
                setCaseFiles(res.data);
                setShowCaseFileModal(true);
            })
            .catch((err) => {

            })
    }

    return (
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem} />
            <h2 className="text-bold text-body-emphasis mb-4">Support Case Manager</h2>
            <div className="chat d-flex gap-0">
                <div className="chat-sidebar border">
                    <div className={cx("chat-sidebar-tab", "border-bottom")}>
                        <div className={cx("tab-header", "border")}>
                            {["PendingTab", "ChatTab"].map((tab) => (
                                <div
                                    key={tab}
                                    className={cx("header-item", activeTab === tab && "active")}
                                    onClick={() => setActiveTab(tab)}
                                >
                                  <span className="fs-9 fw-bold">
                                    {tab === "PendingTab" ? "Pending Cases" : "Chatting Case"}
                                  </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {renderSidebarContent()}
                </div>

                <div className={cx("card-chat", "h-100 w-100 card")}>
                    {
                        Object.keys(selectedChat).length > 0 && (
                            <div className={cx("card-header","card-header px-1 py-2 flex-between-center d-flex")}>
                                <div className=" d-flex align-items-center text-body" data-discover="true">
                                    {
                                        Object.keys(selectedChat).length > 0 && (
                                            <Fragment>
                                                <div className="avatar avatar-xl status-online">
                                                    <img alt="avatar" className="rounded-circle" src={selectedChat.CustomerAvatarUrl}/>
                                                </div>
                                                <p className="mb-0 ms-3 text-body-emphasis fw-semibold">{selectedChat.CustomerName}</p>
                                            </Fragment>
                                        )
                                    }
                                </div>
                                <div className="">
                                    {
                                        Object.keys(selectedChat).length > 0 && (
                                            <div className="dropdown d-inline-block">
                                                <DropdownAction dropdownItems={[
                                                    { text: "View details", icon: faEye},
                                                    { text: "Close chat", icon: faXmark, className: "text-danger", onClick: handleCloseChat},
                                                    {text: "Case files", icon: faFile, onClick: handleOpenCaseFile}
                                                ]}
                                                                isVerticalIcon={true}
                                                />
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        )
                    }
                    {
                        Object.keys(selectedChat).length > 0 && (
                            <div className="p-3 p-sm-4 scrollbar d-flex flex-column gap-2 card-body">
                                {messageGroup.map((group, index) => {
                                    const isReceiver = group[0].SupporterOrCustomer === "Customer";
                                    const time = new Date(group[group.length - 1].CreatedDate).toLocaleString();
                                    return (
                                        <ChatMessageGroup
                                            key={index}
                                            group={group}
                                            isReceiver={isReceiver}
                                            time={time}
                                            avatar={selectedChat.CustomerAvatarUrl}
                                            isLast={(messageGroup.length - 1) === index}
                                        />
                                    );
                                })}
                                <div ref={chatEndRef}></div>
                            </div>
                        )
                    }
                    {
                        Object.keys(selectedChat).length <= 0 && (
                            <div className="p-3 p-sm-4 scrollbar d-flex flex-center flex-column gap-2 card-body">
                                {
                                    noChatImage &&
                                    <img alt="chat" height="260" width="320" className="" src={noChatImage}/>
                                }
                                <span className="mt-3">Select a conversation to start chatting.</span>
                            </div>
                        )
                    }
                    {
                        Object.keys(selectedChat).length > 0 && (
                            <div className="card-footer py-3">
                                <div className={cx("chat-footer", "position-relative")}>
                            <textarea
                                placeholder="Type message here..."
                                className={cx(
                                    "chat-textarea",
                                    "outline-none border-0 resize-none mt-1 px-3 py-2 fs-8-1 fw-semibold scrollbar"
                                )}
                                value={messageInput}
                                onChange={(e) => setMessageInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                            />
                                    <div className={cx("action-send")}>
                                        <Send className={cx("send", "position-absolute")} size={17} />
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
            <CenteredModal show={showModal}
                           title="Reject Support Case"
                           confirmText="Reject"
                           formId="formReject"
                           onClose={() => setShowModal(false)}
            >
                <form onSubmit={handleSubmit(onSubmitFrom)} id="formReject">
                    <div className="mb-3 text-center">
                        <p className="fs-9 fw-bold mb-1">Are you sure you want to reject this support case.</p>
                        <p className="fs-9 fw-bold">Please enter the reject reason.</p>
                    </div>
                    <div className="mb-3">
                        <FormInput showHeaderLabel={true}
                                   name = "RejectReason"
                                   textarea={true}
                                   errors={errors}
                                   required={true}
                                   label="Assign Reject Reason"
                                   validation={{...register("RejectReason", {
                                           required: 'Reject Reason is required.'
                                       })}}
                        />
                    </div>
                </form>

            </CenteredModal>
            <CenteredModal
                show = {showCaseFileModal}
                onClose={() => setShowCaseFileModal(false)}
                title="Case File"
                closeText="Close"
            >
                {
                    caseFiles.length <= 0 && (
                        <div className="text-center">
                            <span>No case file for this session chat.</span>
                        </div>
                    )
                }
                {
                    caseFiles.length > 0 && (
                        caseFiles.map((file, index) => (
                            <FilePreview
                                key={index}
                                showDownloadFile={true}
                                fileSize={file.CaseFileSize}
                                src={file.CaseFileUrl}
                                fileName={file.CaseFileName}
                                disabled={true}
                                createdDate={file.CreatedDate}
                            />
                        ))
                    )
                }
            </CenteredModal>
        </Fragment>
    );
}

export default SupportCaseAdmin;